import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MainComponent } from './main.component';
import { NotFoundComponent } from './not-found.component';
import { SeoPageComponent } from './seo-page/seo-page.component';
import { seoPathMatcher, seoRouteResolver } from './seo-page/seo-route';

// Only the home page, the SEO page matcher and the 404 page ship in the initial bundle.
// Every other page is loaded on navigation, which keeps its code (and libraries such
// as chart.js on /api-insights) out of the first download of every visitor.
export const routes: Routes = [
  {
    path: '',
    component: MainComponent,
    data: {
      title: 'Blauwtand',
      description:
        'Blauwtand is een job board met IT vacatures voor professionals & diensten voor opdrachtgevers. Jouw partner in IT recruitment. Neem contact op.',
      robots: 'index,follow',
    },
  },
  // /vacatures keeps a single stable title regardless of active filters. Filtered forms
  // (?q=, ?location=, ?industries=) canonicalize back to /vacatures (see SeoService) —
  // emitting a per-filter title would leak into the canonical target's rendered head and
  // collide with the SEO landing pages that own keyword intent. Do not introduce
  // runtime title rewriting on this route.
  {
    path: 'vacatures',
    loadComponent: () =>
      import('./vacancies.component').then((m) => m.VacanciesComponent),
    data: {
      breadcrumb: 'IT vacatures voor IT professionals',
      title: 'IT Vacatures voor Professionals | Bekijk jobs | Blauwtand',
      description:
        'Bekijk onze actuele IT vacatures en vind jouw volgende stap. We komen graag met je in contact, IT professional. Blauwtand is jouw partner in IT recruitment.',
      robots: 'index,follow',
    },
  },
  {
    path: 'vacatures/:slug-:id',
    loadComponent: () =>
      import('./view-job.component').then((m) => m.ViewJobComponent),
    data: {
      breadcrumb: 'IT vacatures voor IT professionals',
      title: 'Vacature',
      description:
        'Details van de vacature, verantwoordelijkheden en arbeidsvoorwaarden.',
      robots: 'index,follow',
    },
  },
  {
    path: 'vacatures/bekijk-vacature/:id',
    loadComponent: () =>
      import('./view-job.component').then((m) => m.ViewJobComponent),
    data: {
      breadcrumb: 'IT vacatures voor IT professionals',
      title: 'Vacature',
      description:
        'Details van de vacature, verantwoordelijkheden en arbeidsvoorwaarden.',
      robots: 'index,follow',
    },
  },
  {
    // Legacy short URL: /vacatures/841491
    // Canonical should point to /vacatures/<slug>-<id> and we may client-redirect.
    path: 'vacatures/:id',
    loadComponent: () =>
      import('./view-job.component').then((m) => m.ViewJobComponent),
    data: {
      breadcrumb: 'IT vacatures voor IT professionals',
      title: 'Vacature',
      description:
        'Details van de vacature, verantwoordelijkheden en arbeidsvoorwaarden.',
      // Prefer not indexing the short URL variant.
      robots: 'noindex,follow',
    },
  },
  {
    path: 'vacatures/:slug-:id/solliciteren',
    loadComponent: () =>
      import('./direct-apply.component').then((m) => m.DirectApplyComponent),
    data: {
      breadcrumb: 'Direct solliciteren',
      title: 'Direct solliciteren',
      robots: 'noindex,follow',
      description: 'Direct solliciteren op deze vacature.',
    },
  },
  {
    path: 'vacatures/:slug-:id/solliciteren/voltooid',
    loadComponent: () =>
      import('./components/applied.component').then((m) => m.AppliedComponent),
    data: {
      breadcrumb: 'Sollicitatie verzonden',
      title: 'Sollicitatie verzonden',
      robots: 'noindex,follow',
      description: 'Bevestiging van je sollicitatie.',
    },
  },
  {
    path: 'vacatures/bekijk-vacature/:id/solliciteren',
    loadComponent: () =>
      import('./direct-apply.component').then((m) => m.DirectApplyComponent),
    data: {
      breadcrumb: 'Direct solliciteren',
      title: 'Direct solliciteren',
      robots: 'noindex,follow',
      description: 'Direct solliciteren op deze vacature.',
    },
  },
  {
    path: 'vacatures/bekijk-vacature/:id/solliciteren/voltooid',
    loadComponent: () =>
      import('./components/applied.component').then((m) => m.AppliedComponent),
    data: {
      breadcrumb: 'Sollicitatie verzonden',
      title: 'Sollicitatie verzonden',
      robots: 'noindex,follow',
      description: 'Bevestiging van je sollicitatie.',
    },
  },
  {
    path: 'open-sollicitatie',
    loadComponent: () =>
      import('./apply.component').then((m) => m.ApplyComponent),
    data: {
      breadcrumb: 'Open sollicitatie',
      title: 'Open sollicitatie',
      description: 'Stuur een open sollicitatie naar Blauwtand.',
      robots: 'index,follow',
    },
  },
  {
    path: 'opdrachtgevers',
    loadComponent: () =>
      import('./employers.component').then((m) => m.EmployersComponent),
    data: {
      breadcrumb: 'Opdrachtgevers',
      title: 'Voor opdrachtgevers',
      description:
        'IT diensten voor opdrachtgevers. Vind snel de juiste IT professionals via Blauwtand, jouw recruitmentpartner voor opdrachtgevers. Bekijk meer.',
      robots: 'index,follow',
    },
  },
  {
    path: 'over-blauwtand',
    loadComponent: () =>
      import('./about.component').then((m) => m.AboutComponent),
    data: {
      breadcrumb: 'Over Blauwtand',
      title: 'Over Blauwtand',
      description:
        'Lees meer over Blauwtand, IT vacatures, onze missie & onze dienstverlening. Blauwtand is jouw partner in IT recruitment vanuit Zoetermeer.',
      robots: 'index,follow',
    },
  },
  {
    path: 'over-blauwtand/team/:slug',
    loadComponent: () =>
      import('./team-profile.component').then((m) => m.TeamProfileComponent),
    data: {
      breadcrumb: 'Team',
      title: 'Teamlid',
      description: 'Maak kennis met een van onze teamleden.',
      robots: 'index,follow',
    },
  },
  {
    path: 'werken-bij',
    loadComponent: () =>
      import('./careers.component').then((m) => m.CareersComponent),
    data: {
      breadcrumb: 'Werken bij Blauwtand',
      title: 'Werken bij Blauwtand',
      description:
        "Op zoek naar uitdaging, ontwikkeling én enthousiaste collega's? Sluit je aan bij Blauwtand en help IT-professionals en bedrijven verbinden.",
      robots: 'index,follow',
    },
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./contact.component').then((m) => m.ContactComponent),
    data: {
      breadcrumb: 'Contact',
      title: 'Contact',
      description:
        'Neem contact op met Blauwtand, jouw partner in IT recruitment gevestigd in Zoetermeer. Leer meer.',
      robots: 'index,follow',
    },
  },
  {
    path: 'privacy-statement',
    loadComponent: () =>
      import('./privacy.component').then((m) => m.PrivacyComponent),
    data: {
      breadcrumb: 'Privacyverklaring',
      title: 'Privacyverklaring',
      description: 'Lees de privacyverklaring van Blauwtand.',
      robots: 'index,follow',
    },
  },
  {
    path: 'stories',
    loadComponent: () =>
      import('./blog/blog-list.component').then((m) => m.BlogListComponent),
    data: {
      breadcrumb: 'Stories',
      title: 'Blauwtand Stories',
      description:
        'Ontdek verhalen, inzichten & praktische tips over IT professionals en de arbeidsmarkt. We vertellen je graag meer over al onze ervaringen in IT recruitment.',
      robots: 'index,follow',
    },
  },
  {
    path: 'stories/:slug',
    loadComponent: () =>
      import('./blog/blog-detail.component').then((m) => m.BlogDetailComponent),
    data: {
      breadcrumb: 'Stories',
      title: 'Story',
      description: 'Lees het volledige artikel van Blauwtand.',
      robots: 'index,follow',
    },
  },
  {
    // Author archive — /auteur/<team-member-slug>. 'auteur' is a protected root, so the
    // SEO matcher below never claims it. (BLA-5)
    path: 'auteur/:slug',
    loadComponent: () =>
      import('./author/author-page.component').then((m) => m.AuthorPageComponent),
    data: {
      breadcrumb: 'Auteur',
      title: 'Auteur',
      description: 'Alle artikelen van deze auteur bij Blauwtand.',
      robots: 'index,follow',
    },
  },
  {
    path: 'api-insights',
    loadComponent: () =>
      import('./logs/logs.component').then((m) => m.LogsComponent),
    data: {
      breadcrumb: 'API-inzichten',
      title: 'API-inzichten',
      robots: 'noindex,follow',
    },
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
    data: {
      title: 'Admin',
      robots: 'noindex,nofollow',
    },
  },
  { path: 'home', redirectTo: '/', pathMatch: 'full' },
  // Legacy redirects (old English/previous paths -> new Dutch paths)
  { path: 'vacancies', redirectTo: 'vacatures', pathMatch: 'full' },
  {
    path: 'vacancies/:slug-:id',
    redirectTo: 'vacatures/:slug-:id',
    pathMatch: 'full',
  },
  {
    path: 'vacancies/view-job/:id',
    redirectTo: 'vacatures/bekijk-vacature/:id',
    pathMatch: 'full',
  },
  {
    path: 'vacancies/:slug-:id/direct-apply',
    redirectTo: 'vacatures/:slug-:id/solliciteren',
    pathMatch: 'full',
  },
  {
    path: 'vacancies/view-job/:id/direct-apply',
    redirectTo: 'vacatures/bekijk-vacature/:id/solliciteren',
    pathMatch: 'full',
  },
  {
    path: 'vacancies/view-job/:id/direct-apply/success',
    redirectTo: 'vacatures/bekijk-vacature/:id/solliciteren/voltooid',
    pathMatch: 'full',
  },
  {
    path: 'open-application',
    redirectTo: 'open-sollicitatie',
    pathMatch: 'full',
  },
  { path: 'employers', redirectTo: 'opdrachtgevers', pathMatch: 'full' },
  { path: 'werkgevers', redirectTo: 'opdrachtgevers', pathMatch: 'full' },
  { path: 'about', redirectTo: 'over-blauwtand', pathMatch: 'full' },
  { path: 'over', redirectTo: 'over-blauwtand', pathMatch: 'full' },
  {
    path: 'over/team/:slug',
    redirectTo: 'over-blauwtand/team/:slug',
    pathMatch: 'full',
  },
  // BLA-54 SEO pages: any path outside the fixed namespaces above (1-4 levels). The
  // full-path resolve API decides page / alias / missing; the page owns its metadata.
  {
    matcher: seoPathMatcher,
    component: SeoPageComponent,
    resolve: { seo: seoRouteResolver },
    data: {
      title: 'Blauwtand',
      robots: 'index,follow',
      seoOwner: true,
    },
  },
  {
    path: '**',
    component: NotFoundComponent,
    data: {
      title: 'Pagina niet gevonden',
      description: 'Deze pagina bestaat niet of is verplaatst.',
      robots: 'noindex,follow',
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class RoutingModule {}
